import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import debounce from 'lodash/debounce';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { useToast } from '../shared/Toast';
import { useAccount } from '../../hooks/useAccount';
import { useSchedulingOptions } from '../../hooks/useSchedulingOptions';
import { useDebounce } from '../../hooks/useDebounce';
import { Accordion } from '../shared/Accordion';
import { LivePreviewCanvas } from './LivePreviewCanvas';
import { CaptionEditor } from './CaptionEditor';
import { StylePanel } from './StylePanel';
import { AlignmentSelector } from './AlignmentSelector';
import { AIAssistant } from './AIAssistant';
import { SchedulingAccordion } from './SchedulingAccordion';
import GiphyPanel from './GiphyPanel';
import GIFControls from './GIFControls';
import './GiphyPanel.css';
import { CanvasColorEditor } from './CanvasColorEditor';
import { AspectRatioIndicator } from './AspectRatioIndicator';
import VideoTemplatePreview from './VideoTemplatePreview';
import TemplateVariableEditor from './TemplateVariableEditor';
import { VisualEffectsPanel } from './VisualEffectsPanel';
import { GlobalAISuggestionBanner } from './GlobalAISuggestionBanner';
import { RemotionPreview } from './RemotionPreview';
import { BackgroundSelector } from './BackgroundSelector';
import { TextAnimationSelector } from './TextAnimationSelector';
import { ProductFontSelector } from './ProductFontSelector';
import axiosInstance, { api } from '../../services/api';
import { sanitizeCaption, sanitizeHexColor, sanitizeInput } from '../../utils/sanitization';
import { saveGifSticker, removeGifSticker } from '../../api/gifApi';
import { showFeatureLockedModal } from '../../utils/subscriptionModals';

/**
 * ApprovalModalV2 Component
 *
 * A comprehensive modal for editing and approving Instagram Story captions
 * with the new "Edit-First" UX paradigm introduced in Week 3.
 *
 * Features:
 * - Live preview with real-time updates
 * - Auto-saving caption editor
 * - Style customization panel
 * - AI-powered caption regeneration
 * - Responsive two-column layout
 */
export const ApprovalModalV2 = ({
  item = null,
  isOpen,
  onClose,
  onApprove = () => {},
  onUpdate = () => {},
  onRejectImage = () => {},
  onApproveVideo = () => {},
  onDeleteVideo = () => {},
  onApproveProduct = () => {},
  onRejectProduct = () => {}
}) => {
  // Hooks
  const { t } = useTranslation('approval');
  const toast = useToast();
  const { selectedAccountId } = useAccount();

  // Scheduling options hook (same for images and videos - quiet hours are account-based)
  const scheduling = useSchedulingOptions(selectedAccountId, isOpen);

  // Type detection
  const isVideo = item?.type === 'video' || !!item?.video_id;
  const isProduct = item?.type === 'product' || !!item?.proposal_id;
  const [isApprovingVideo, setIsApprovingVideo] = useState(false);
  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Product proposal state
  const [isApprovingProduct, setIsApprovingProduct] = useState(false);
  const [isRejectingProduct, setIsRejectingProduct] = useState(false);
  const [showRejectProductConfirm, setShowRejectProductConfirm] = useState(false);
  const [productRejectReason, setProductRejectReason] = useState('');
  const [productCaption, setProductCaption] = useState(item?.caption_text || '');
  const [captionColor, setCaptionColor] = useState(item?.caption_color || '#ffffff');
  const [isRegeneratingCaption, setIsRegeneratingCaption] = useState(false);
  const [templateVariables, setTemplateVariables] = useState(item?.template_variables || {});
  const [selectedTemplate, setSelectedTemplate] = useState(item?.template_id || 'MinimalistReveal');
  const [aiSuggestionApplied, setAiSuggestionApplied] = useState(false);
  const [visualEffects, setVisualEffects] = useState(item?.effects || {
    sparkles: true,
    floatingShapes: true,
    bokeh: true,
    ambientGlow: true,
    priceShimmer: true,
    pulsingGlow: false,
  });

  // Background configuration (LLM-suggested, user-switchable)
  const [backgroundType, setBackgroundType] = useState(
    item?.template_variables?.background_type || item?.llm_suggestion?.style?.backgroundType || 'aurora'
  );
  const [backgroundPalette, setBackgroundPalette] = useState(
    item?.template_variables?.background_palette || item?.llm_suggestion?.style?.backgroundPalette || 'aurora'
  );

  // Text animation configuration (LLM-suggested, user-switchable)
  const [textAnimation, setTextAnimation] = useState(
    item?.template_variables?.text_animation || item?.llm_suggestion?.style?.textAnimation || 'typewriter'
  );

  // Caption font configuration (user-selectable, subscription-based)
  const [captionFont, setCaptionFont] = useState(
    item?.template_variables?.caption_font || item?.llm_suggestion?.style?.captionFont || 'Modern'
  );

  // State management
  const [caption, setCaption] = useState(item?.caption_text || '');
  const [originalCaption, setOriginalCaption] = useState(item?.caption_text || '');

  // Debounced caption for LivePreviewCanvas to prevent Chrome crash
  // The canvas redraw is expensive (26+ fillText calls per line for outlines)
  // Without debouncing, fast typing causes 10+ redraws/second → Chrome crash
  const debouncedCaptionForPreview = useDebounce(caption, 100);
  const [style, setStyle] = useState({
    textColor: item?.text_color_hex || '#FFFFFF',
    backgroundColor: item?.text_background_color_hex || '#000000',
    textHasBackground: item?.text_has_background || false
  });
  const [originalStyle, setOriginalStyle] = useState({
    textColor: item?.text_color_hex || '#FFFFFF',
    backgroundColor: item?.text_background_color_hex || '#000000',
    textHasBackground: item?.text_has_background || false
  });
  const [textPosition, setTextPosition] = useState(
    item?.text_position || item?.caption?.text_position || { x: 540, y: 1700 }
  );
  const [originalPosition, setOriginalPosition] = useState(
    item?.text_position || item?.caption?.text_position || { x: 540, y: 1700 }
  );
  const [textAlignment, setTextAlignment] = useState(
    item?.text_alignment || item?.caption?.text_alignment || 'left'
  );
  const [originalAlignment, setOriginalAlignment] = useState(
    item?.text_alignment || item?.caption?.text_alignment || 'left'
  );
  const [hasStyleChanges, setHasStyleChanges] = useState(false);
  const [hasCaptionChanges, setHasCaptionChanges] = useState(false);
  const [hasPositionChanges, setHasPositionChanges] = useState(false);
  const [hasAlignmentChanges, setHasAlignmentChanges] = useState(false);
  const [hasBackgroundChanges, setHasBackgroundChanges] = useState(false);
  const [hasFontChanges, setHasFontChanges] = useState(false);
  const [hasGifChanges, setHasGifChanges] = useState(false);

  // Font state
  const [selectedFont, setSelectedFont] = useState(item?.font_name || 'Classic');
  const [originalFont, setOriginalFont] = useState(item?.font_name || 'Classic');

  // Canvas background state (for non-9:16 images)
  const [backgroundMode, setBackgroundMode] = useState(
    item?.background_metadata?.background_type || 'solid'
  );
  const [backgroundConfig, setBackgroundConfig] = useState({
    // Solid color
    backgroundColor: item?.background_metadata?.background_color || '#1E1E1E',
    // Gradient
    gradientType: item?.background_metadata?.gradient_type || 'linear',
    gradientColors: item?.background_metadata?.gradient_colors || ['#FF0080', '#7928CA'],
    gradientAngle: item?.background_metadata?.gradient_angle || 45
  });
  const [originalBackgroundConfig, setOriginalBackgroundConfig] = useState({
    mode: item?.background_metadata?.background_type || 'solid',
    backgroundColor: item?.background_metadata?.background_color || '#1E1E1E',
    gradientType: item?.background_metadata?.gradient_type || 'linear',
    gradientColors: item?.background_metadata?.gradient_colors || ['#FF0080', '#7928CA'],
    gradientAngle: item?.background_metadata?.gradient_angle || 45
  });
  const [hasWrongAspectRatio, setHasWrongAspectRatio] = useState(
    item?.background_metadata?.needs_background || false
  );

  // Backward compatibility: Keep canvasBackgroundColor for LivePreviewCanvas
  const canvasBackgroundColor = backgroundMode === 'solid'
    ? backgroundConfig.backgroundColor
    : null;
  const [isSaving, setIsSaving] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [brandColors, setBrandColors] = useState(null);
  const [gifMetadata, setGifMetadata] = useState(null);
  const [showGiphyPanel, setShowGiphyPanel] = useState(false);
  const [isSavingGif, setIsSavingGif] = useState(false);
  const [isRemovingGif, setIsRemovingGif] = useState(false);

  // Delete image state
  const [deleteReason, setDeleteReason] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Update state when item prop changes
  useEffect(() => {
    if (item) {
      const initialCaption = item.caption_text || '';
      setCaption(initialCaption);
      setOriginalCaption(initialCaption);
      setHasCaptionChanges(false);

      const initialStyle = {
        textColor: item.text_color_hex || '#FFFFFF',
        backgroundColor: item.text_background_color_hex || '#000000',
        textHasBackground: item.text_has_background || false
      };
      setStyle(initialStyle);
      setOriginalStyle(initialStyle);
      setHasStyleChanges(false);

      const initialPosition = item.text_position || item.caption?.text_position || { x: 540, y: 1700 };
      setTextPosition(initialPosition);
      setOriginalPosition(initialPosition);
      setHasPositionChanges(false);

      const initialAlignment = item.text_alignment || item.caption?.text_alignment || 'left';
      setTextAlignment(initialAlignment);
      setOriginalAlignment(initialAlignment);
      setHasAlignmentChanges(false);

      const initialFont = item.font_name || 'Classic';
      setSelectedFont(initialFont);
      setOriginalFont(initialFont);
      setHasFontChanges(false);

      // Load GIF metadata if present (backend returns GifStickerMetadata)
      const initialGifMetadata = item.GifStickerMetadata
        ? (typeof item.GifStickerMetadata === 'string'
            ? JSON.parse(item.GifStickerMetadata)
            : item.GifStickerMetadata)
        : null;
      setGifMetadata(initialGifMetadata);

      // Product proposal state
      setProductCaption(item.caption_text || '');
      setCaptionColor(item.caption_color || '#ffffff');
      setTemplateVariables(item.template_variables || {});
      setSelectedTemplate(item.template_id || 'MinimalistReveal');
      setAiSuggestionApplied(false);
      setVisualEffects(item.effects || item.llm_suggestion?.effects || {
        sparkles: true,
        floatingShapes: true,
        bokeh: true,
        ambientGlow: true,
        priceShimmer: true,
        pulsingGlow: false,
      });
      setProductRejectReason('');
    }
  }, [item]);

  // Debounced auto-save for GIF metadata changes (position, size, opacity)
  const debouncedSaveGif = useMemo(
    () => debounce(async (metadata) => {
      if (!item?.image_id || !item?.sentiment || !item?.instagram_account_id) return;

      try {
        await saveGifSticker(item.image_id, metadata, item.sentiment, item.instagram_account_id);
        console.log('[GIF Auto-save] GIF metadata saved successfully');
      } catch (error) {
        console.error('[GIF Auto-save] Failed to save GIF metadata:', error);
        // Don't show toast for auto-save failures to avoid annoying users
        // User can manually re-save by clicking the add button again
      }
    }, 1000), // 1 second debounce
    [item?.image_id, item?.sentiment, item?.instagram_account_id]
  );

  // Auto-save GIF metadata when it changes (drag position, size, opacity)
  useEffect(() => {
    // Only auto-save if:
    // 1. GIF metadata exists (user has added a GIF)
    // 2. Not currently saving or removing
    // 3. Modal is open (isOpen is true via item prop)
    if (gifMetadata && !isSavingGif && !isRemovingGif && item) {
      debouncedSaveGif(gifMetadata);
    }
  }, [gifMetadata, isSavingGif, isRemovingGif, item, debouncedSaveGif]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSaveGif.cancel();
    };
  }, [debouncedSaveGif]);

  // Fetch brand settings when modal opens
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchBrandSettings = async () => {
      // Get account_id from selectedAccountId (since item doesn't have it)
      const accountId = selectedAccountId;

      if (!accountId || !isOpen) return;

      try {
        const settings = await api.getBrandSettings(accountId, { signal: abortController.signal });
        if (isMounted) {
          setBrandColors({
            textColor: settings.text_color || '#FFFFFF',
            backgroundColor: settings.background_color || '#000000',
            textHasBackground: settings.use_background || false
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          console.error('Failed to load brand colors:', error);
          // Silently fail - brand colors are optional
        }
      }
    };

    fetchBrandSettings();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [selectedAccountId, isOpen]);

  /**
   * Handle caption text changes (for live preview only)
   * CaptionEditor handles its own auto-save internally
   */
  const handleCaptionChange = useCallback((newCaption) => {
    setCaption(newCaption);

    // Check if caption differs from original
    const hasChanges = newCaption.trim() !== originalCaption.trim();
    setHasCaptionChanges(hasChanges);
  }, [originalCaption]);

  /**
   * Handle font changes
   */
  const handleFontChange = useCallback((fontId) => {
    setSelectedFont(fontId);

    // Check if font differs from original
    const hasChanges = fontId !== originalFont;
    setHasFontChanges(hasChanges);
  }, [originalFont]);

  /**
   * Handle style changes
   */
  const handleStyleChange = useCallback(async (updates) => {
    const newStyle = { ...style, ...updates };
    setStyle(newStyle);

    // Check if style differs from original
    const hasChanges =
      newStyle.textColor !== originalStyle.textColor ||
      newStyle.backgroundColor !== originalStyle.backgroundColor ||
      newStyle.textHasBackground !== originalStyle.textHasBackground;

    setHasStyleChanges(hasChanges);

    // Save style changes to API
    setIsSaving(true);
    try {
      // Include caption_id if available
      const captionId = item.caption_id || item.current_caption_id;

      // Sanitize style colors before sending
      const sanitizedStyle = {
        ...newStyle,
        textColor: sanitizeHexColor(newStyle.textColor),
        backgroundColor: sanitizeHexColor(newStyle.backgroundColor),
      };

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        caption: { text: sanitizeCaption(caption) },
        style: sanitizedStyle,
        ...(captionId && { previousCaptionId: captionId })
      });
    } catch (error) {
      console.error('Style save error:', error);
      toast.error(t('modal.toast.styleError'));
    } finally {
      setIsSaving(false);
    }
  }, [caption, style, originalStyle, item, toast, t]);

  /**
   * Handle text position changes from drag-and-drop
   */
  const handlePositionChange = useCallback(async (newPosition) => {
    setTextPosition(newPosition);

    // Check if position differs from original
    const hasChanges =
      newPosition.x !== originalPosition.x ||
      newPosition.y !== originalPosition.y;

    setHasPositionChanges(hasChanges);

    // Save position changes to API (auto-save)
    setIsSaving(true);
    try {
      const captionId = item.caption_id || item.current_caption_id;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        style: { textPosition: newPosition },
        ...(captionId && { previousCaptionId: captionId })
      });
    } catch (error) {
      console.error('Position save error:', error);
      toast.error(t('modal.toast.positionError'));
    } finally {
      setIsSaving(false);
    }
  }, [originalPosition, item, toast, t]);

  /**
   * Handle reset text position to original
   */
  const handleResetPosition = useCallback(async () => {
    setTextPosition(originalPosition);
    setHasPositionChanges(false);

    // Save reset to API
    setIsSaving(true);
    try {
      const captionId = item.caption_id || item.current_caption_id;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        style: { textPosition: originalPosition },
        resetPosition: true,
        ...(captionId && { previousCaptionId: captionId })
      });

      toast.success(t('modal.toast.positionReset'));
    } catch (error) {
      console.error('Position reset error:', error);
      toast.error(t('modal.toast.positionResetError'));
    } finally {
      setIsSaving(false);
    }
  }, [originalPosition, item, toast, t]);

  /**
   * Handle text alignment changes
   */
  const handleAlignmentChange = useCallback(async (newAlignment) => {
    setTextAlignment(newAlignment);

    // Check if alignment differs from original
    const hasChanges = newAlignment !== originalAlignment;
    setHasAlignmentChanges(hasChanges);

    // Save alignment changes to API (auto-save)
    setIsSaving(true);
    try {
      const captionId = item.caption_id || item.current_caption_id;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'save_draft',
        instagram_account_id: selectedAccountId,
        style: { textAlignment: newAlignment },
        ...(captionId && { previousCaptionId: captionId })
      });

      toast.success('Textausrichtung gespeichert');
    } catch (error) {
      console.error('Alignment save error:', error);
      toast.error('Fehler beim Speichern der Textausrichtung');
    } finally {
      setIsSaving(false);
    }
  }, [originalAlignment, item, toast]);

  /**
   * Handle background mode change (solid <-> gradient)
   */
  const handleBackgroundModeChange = useCallback((newMode) => {
    setBackgroundMode(newMode);

    // When switching to gradient, initialize with current solid color if needed
    if (newMode === 'gradient' && backgroundConfig.gradientColors.length === 0) {
      setBackgroundConfig(prev => ({
        ...prev,
        gradientColors: [prev.backgroundColor, '#FFFFFF']
      }));
    }
  }, [backgroundConfig.backgroundColor, backgroundConfig.gradientColors.length]);

  /**
   * Handle solid color change (live preview)
   */
  const handleSolidColorChange = useCallback((newColor) => {
    setBackgroundConfig(prev => ({
      ...prev,
      backgroundColor: newColor
    }));
  }, []);

  /**
   * Handle gradient configuration change (live preview)
   */
  const handleGradientChange = useCallback((updates) => {
    setBackgroundConfig(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  /**
   * Track background changes (mode or configuration)
   */
  useEffect(() => {
    // Check if mode changed
    const modeChanged = backgroundMode !== originalBackgroundConfig.mode;

    // Check if solid color changed
    const solidColorChanged = backgroundMode === 'solid' &&
      backgroundConfig.backgroundColor !== originalBackgroundConfig.backgroundColor;

    // Check if gradient config changed
    const gradientChanged = backgroundMode === 'gradient' && (
      backgroundConfig.gradientType !== originalBackgroundConfig.gradientType ||
      backgroundConfig.gradientAngle !== originalBackgroundConfig.gradientAngle ||
      JSON.stringify(backgroundConfig.gradientColors) !== JSON.stringify(originalBackgroundConfig.gradientColors)
    );

    const hasChanges = modeChanged || solidColorChanged || gradientChanged;
    setHasBackgroundChanges(hasChanges);
  }, [backgroundMode, backgroundConfig, originalBackgroundConfig]);

  // Track GIF changes
  useEffect(() => {
    // If gifMetadata exists, user has added or modified a GIF
    setHasGifChanges(gifMetadata !== null);
  }, [gifMetadata]);

  /**
   * Handle caption approval
   */
  const handleApprove = async () => {
    setIsApproving(true);
    try {
      // Execute cascade reschedule if needed (silent)
      if (scheduling.cascadeInfo.needsCascade) {
        try {
          await scheduling.executeCascadeReschedule();
          console.log('[ApprovalModalV2] Cascade reschedule executed successfully');
        } catch (cascadeError) {
          console.error('[ApprovalModalV2] Cascade reschedule failed:', cascadeError);
          toast.error(t('scheduling.cascadeError'));
          setIsApproving(false);
          return;
        }
      }

      // Include caption_id if available
      const captionId = item.caption_id || item.current_caption_id;

      // Sanitize caption and style before approving
      const sanitizedStyle = {
        ...style,
        textColor: sanitizeHexColor(style.textColor),
        backgroundColor: sanitizeHexColor(style.backgroundColor),
      };

      // Include scheduled_time if manual mode is active
      const scheduledTime = scheduling.isManualMode && scheduling.manualScheduledTime
        ? scheduling.manualScheduledTime.toISOString()
        : null;

      await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'approve',
        instagram_account_id: selectedAccountId,
        caption: { text: sanitizeCaption(caption) },
        style: sanitizedStyle,
        ...(captionId && { previousCaptionId: captionId }),
        ...(scheduledTime && { scheduled_time: scheduledTime })
      });

      toast.success(t('modal.toast.approved'));

      // Call parent callbacks
      if (onApprove) {
        onApprove(item);
      }
      onClose();
    } catch (error) {
      console.error('Approval error:', error);
      toast.error(t('modal.toast.approvalError', { error: error.message }));
    } finally {
      setIsApproving(false);
    }
  };

  /**
   * Handle video approval - delegate to parent component
   */
  const handleApproveVideoClick = async () => {
    setIsApprovingVideo(true);
    try {
      // Validate video structure
      if (!item?.video_id) {
        throw new Error('Invalid video: missing video_id');
      }

      // Call parent callback (ApprovalPage.handleApproveVideo)
      if (onApproveVideo) {
        await Promise.resolve(onApproveVideo(item));
        toast.success(t('modal.toast.videoApproved', { defaultValue: 'Video approved!' }));
      } else {
        throw new Error('No approval callback provided');
      }

      onClose();
    } catch (error) {
      console.error('Video approval error:', error);
      toast.error(t('modal.toast.videoApprovalError', {
        defaultValue: 'Failed to approve video',
        error: error.message
      }));
    } finally {
      setIsApprovingVideo(false);
    }
  };

  /**
   * Handle video deletion - delegate to parent component
   */
  const handleDeleteVideoClick = async () => {
    setIsDeletingVideo(true);
    try {
      // Validate video structure
      if (!item?.video_id) {
        throw new Error('Invalid video: missing video_id');
      }

      // Call parent callback (ApprovalPage.handleDeleteVideo)
      if (onDeleteVideo) {
        await Promise.resolve(onDeleteVideo(item));
        toast.success(t('modal.toast.videoDeleted', { defaultValue: 'Video deleted' }));
      } else {
        throw new Error('No delete callback provided');
      }

      onClose();
    } catch (error) {
      console.error('Video deletion error:', error);
      toast.error(t('modal.toast.videoDeletionError', {
        defaultValue: 'Failed to delete video',
        error: error.message
      }));
    } finally {
      setIsDeletingVideo(false);
      setShowDeleteConfirm(false);
    }
  };

  /**
   * Handle AI regeneration (from Quick Actions or custom feedback)
   */
  const handleRegenerate = async ({ feedback, aiRequest }) => {
    setIsRegenerating(true);
    try {
      // Sanitize AI request/feedback text
      // aiRequest can be a string or an object with {feedback, temperature}
      const requestText = typeof aiRequest === 'object' ? aiRequest.feedback : (aiRequest || feedback);
      const sanitizedRequest = sanitizeInput(requestText, 500);

      const response = await axiosInstance.post(`/captions/${item.image_id}/update`, {
        action: 'regenerate',
        instagram_account_id: selectedAccountId,
        aiRequest: sanitizedRequest
      });

      // Update caption with regenerated text
      if (response.data?.caption?.text) {
        setCaption(response.data.caption.text);
        toast.success(t('modal.toast.regenerated'));
      }

      // Trigger parent update callback if provided
      if (onUpdate) {
        onUpdate();
      }

      // Close modal after successful regeneration
      onClose();
    } catch (error) {
      console.error('Regeneration error:', error);
      toast.error(t('modal.toast.regenerateError', { error: error.message }));
    } finally {
      setIsRegenerating(false);
    }
  };

  /**
   * Handle style regeneration (re-render image with new colors)
   */
  const handleRegenerateWithStyle = async () => {
    setIsRegenerating(true);
    try {
      // Send caption_id explicitly since current_caption_id might be null
      const captionId = item.caption_id || item.current_caption_id;

      // Sanitize caption and style before regenerating
      // Include textPosition and textAlignment for position/alignment changes
      const sanitizedStyle = {
        ...style,
        textColor: sanitizeHexColor(style.textColor),
        backgroundColor: sanitizeHexColor(style.backgroundColor),
        textPosition: textPosition,
        textAlignment: textAlignment
      };

      const payload = {
        action: 'regenerate',
        caption: { text: sanitizeCaption(caption) },
        style: sanitizedStyle,
        colorOnly: true, // Tell backend to only re-render with new colors/position
        previousCaptionId: captionId, // Send caption_id explicitly
        instagram_account_id: selectedAccountId,
        font_name: selectedFont
      };

      // Add background config if background was changed
      if (hasBackgroundChanges) {
        payload.backgroundUpdate = true;
        payload.backgroundConfig = {
          background_type: backgroundMode,
          ...(backgroundMode === 'solid'
            ? { background_color: backgroundConfig.backgroundColor }
            : {
                gradient_type: backgroundConfig.gradientType,
                gradient_colors: backgroundConfig.gradientColors,
                gradient_angle: backgroundConfig.gradientAngle
              }
          )
        };
      }

      await axiosInstance.post(`/captions/${item.image_id}/update`, payload);

      toast.success(t('modal.toast.rerendering'));

      // Close modal and trigger parent update
      if (onUpdate) {
        onUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Style regeneration error:', error);
      toast.error(t('modal.toast.regenerateError', { error: error.message }));
    } finally {
      setIsRegenerating(false);
    }
  };

  /**
   * Handle image deletion
   */
  const handleDeleteImage = async () => {
    const trimmedReason = deleteReason.trim();
    if (!trimmedReason) {
      toast.error(t('modal.delete.reasonRequired'));
      return;
    }

    setIsDeleting(true);
    try {
      // Call parent callback to handle deletion
      await onRejectImage(item, trimmedReason);

      toast.success(t('modal.delete.success'));
      onClose();
    } catch (error) {
      console.error('Delete image error:', error);
      toast.error(t('modal.delete.failed', { error: error.message }));
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle GIF selection
   */
  const handleSelectGif = async (gif) => {
    const previousGif = gifMetadata;  // Store previous state
    setGifMetadata(gif);
    setShowGiphyPanel(false);
    setIsSavingGif(true);

    try {
      await saveGifSticker(item.image_id, gif, item.sentiment, item.instagram_account_id);
      toast.success(t('upload:gif.savedSuccess') || 'GIF sticker saved');
    } catch (error) {
      console.error('Failed to save GIF:', error);
      toast.error(t('errors.gifSaveError') || 'Failed to save GIF sticker');
      setGifMetadata(previousGif);  // Rollback on error
    } finally {
      setIsSavingGif(false);
    }
  };

  /**
   * Handle GIF removal
   */
  const handleRemoveGif = async () => {
    const previousGif = gifMetadata;  // Store previous state for rollback
    setIsRemovingGif(true);

    try {
      await removeGifSticker(item.image_id, item.sentiment, item.instagram_account_id);
      setGifMetadata(null);
      toast.success(t('upload:gif.removedSuccess') || 'GIF sticker removed');
    } catch (error) {
      console.error('Failed to remove GIF:', error);
      toast.error(t('errors.gifRemoveError') || 'Failed to remove GIF sticker');
      setGifMetadata(previousGif);  // Rollback on error
    } finally {
      setIsRemovingGif(false);
    }
  };

  /**
   * Handle GIF sticker button click - checks feature access before opening Giphy panel
   * Phase 5.6: Feature access check for GIF rendering
   */
  const handleOpenGiphyPanel = async () => {
    try {
      // Check if user has access to GIF feature
      if (selectedAccountId) {
        const subscriptionInfo = await api.getAccountSubscription(selectedAccountId);
        const hasGifAccess = subscriptionInfo?.features?.gif_rendering === true;

        if (!hasGifAccess) {
          // Feature locked - show feature locked modal
          showFeatureLockedModal({
            feature_id: 'gif_rendering',
            message: t('featureLocked.defaultDescription') || 'GIFs in your Stories is not available in your current plan'
          });
          return;
        }
      }

      // Feature access granted - open Giphy panel
      setShowGiphyPanel(true);
    } catch (error) {
      console.error('Failed to check GIF feature access:', error);
      // On error, deny access (fail closed for security)
      showFeatureLockedModal({
        feature_id: 'gif_rendering',
        message: t('featureLocked.defaultDescription') || 'GIFs in your Stories is not available in your current plan'
      });
    }
  };

  /**
   * Handle modal close
   */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!item) return null;

  // Get ORIGINAL image URL (NOT the rendered one with caption!)
  // Priority: Original images first, rendered versions as fallback
  // Backend provides original_sas_url for unrendered images and sas_url for rendered images.
  const imageUrl =
    item.original_sas_url ||
    item.caption?.original_sas_url ||
    item.sas_url ||
    item.caption?.sas_url ||
    item.blob_url ||
    item.blob_path ||
    item.preview_url ||
    item.caption?.rendered_sas_url ||
    item.rendered_sas_url ||
    item.rendered_blob_path;

  // Get text rendering parameters from backend
  // Note: textPosition and textAlignment are managed as state to support manual overrides
  const textAreaWidth = item.text_area_width || item.caption?.text_area_width || 800;
  const textAreaHeight = item.text_area_height || item.caption?.text_area_height || 400;
  const fontSize = item.recommended_font_size || item.caption?.recommended_font_size || 48;

  // Render video approval modal separately
  if (isVideo) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        size="5xl"
        title={item.file_name || t('modal.title')}
        closeOnBackdrop={false}
        closeOnEscape={!isApprovingVideo && !isDeletingVideo}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 -mx-6 -my-4 lg:h-[calc(100vh-160px)]">
          {/* Left Column - Video Preview */}
          <div className="lg:sticky lg:top-0 lg:self-start bg-gray-50 p-6 lg:h-[calc(100vh-160px)] flex items-center justify-center">
            <div className="w-full max-w-sm">
              <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
                {t('modal.preview.title')}
              </h3>
              <div className="bg-gray-900 rounded-lg overflow-hidden">
                <video
                  src={item.blob_url}
                  controls
                  className="w-full h-auto"
                  style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 280px)' }}
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>

          {/* Right Column - Content & Controls */}
          <div className="flex flex-col lg:h-[calc(100vh-160px)]">
            {/* Scrollable Content */}
            <div className="flex-1 p-6 space-y-6 overflow-y-auto overscroll-contain">
              {/* Video Metadata */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('modal.video.duration', 'Duration')}:</span>
                  <span className="font-medium">
                    {Math.floor(item.duration / 60)}:{Math.floor(item.duration % 60).toString().padStart(2, '0')}
                  </span>
                </div>
                {item.file_size && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('modal.video.fileSize', 'File Size')}:</span>
                    <span className="font-medium">
                      {(item.file_size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                  </div>
                )}
                {item.resolution && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('modal.video.resolution', 'Resolution')}:</span>
                    <span className="font-medium">{item.resolution}</span>
                  </div>
                )}
                {item.codec && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('modal.video.codec', 'Codec')}:</span>
                    <span className="font-medium">{item.codec}</span>
                  </div>
                )}
                {item.fps && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('modal.video.fps', 'FPS')}:</span>
                    <span className="font-medium">{item.fps.toFixed(1)}</span>
                  </div>
                )}
                {item.has_audio !== undefined && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('modal.video.audio', 'Audio')}:</span>
                    <span className="font-medium">{item.has_audio ? 'Yes' : 'No'}</span>
                  </div>
                )}
              </div>

              {/* Info: Editing not available for videos yet */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                <p>{t('modal.video.editingNotAvailable', 'ℹ️ Video editing features will be available in a future release. For now, you can approve or delete this video.')}</p>
              </div>

              {/* Scheduling Accordion for Videos */}
              <SchedulingAccordion
                autoScheduledTime={scheduling.autoScheduledTime}
                manualScheduledTime={scheduling.manualScheduledTime}
                scheduledTime={scheduling.scheduledTime}
                isManualMode={scheduling.isManualMode}
                cascadeInfo={scheduling.cascadeInfo}
                postIntervalHours={scheduling.postIntervalHours}
                isLoading={scheduling.isLoading}
                error={scheduling.error}
                onManualTimeChange={scheduling.setManualTime}
                onClearManualTime={scheduling.clearManualTime}
                disabled={isApprovingVideo || isDeletingVideo}
              />

              {/* Delete Video Accordion */}
              <Accordion
                title={t('modal.video.deleteTitle', 'Delete Video from Approval Queue')}
                icon="⚠️"
                defaultExpanded={false}
                variant="danger"
              >
                <div className="space-y-4 pt-4">
                  {/* Warning Message */}
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <svg
                      className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-sm text-red-800">
                      {t('modal.video.deleteWarning', 'This action cannot be undone. The video will be permanently removed from the approval queue and will not be posted to Instagram.')}
                    </p>
                  </div>

                  {/* Delete Button */}
                  <Button
                    variant="danger"
                    onClick={handleDeleteVideoClick}
                    loading={isDeletingVideo}
                    disabled={isDeletingVideo}
                    className="w-full"
                  >
                    {isDeletingVideo ? t('modal.video.deleting', 'Deleting...') : t('modal.video.deleteButton', 'Delete Video Permanently')}
                  </Button>
                </div>
              </Accordion>
            </div>

            {/* Sticky Action Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 shadow-lg">
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isApprovingVideo || isDeletingVideo}
                >
                  {t('modal.button.cancel', 'Cancel')}
                </Button>

                <Button
                  variant="primary"
                  onClick={handleApproveVideoClick}
                  loading={isApprovingVideo}
                  disabled={isDeletingVideo}
                >
                  {isApprovingVideo ? t('modal.button.approving', 'Approving...') : t('modal.button.approve', 'Approve & Post')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  // Render product proposal approval modal
  if (isProduct) {
    const productTitle = templateVariables?.product_title || item.template_variables?.product_title || item.product_id || 'Product';
    const salePrice = templateVariables?.sale_price || item.template_variables?.sale_price;
    const originalPrice = templateVariables?.original_price || item.template_variables?.original_price;
    const discountPercent = templateVariables?.discount_percent || item.template_variables?.discount_percent;
    const isRendering = item.status === 'rendering_video';
    const previewUrl = item.preview_url || item.sas_url;

    const handleApproveProductClick = async () => {
      setIsApprovingProduct(true);
      try {
        await onApproveProduct(item, {
          caption_text: productCaption,
          caption_color: captionColor,
          caption_font: captionFont,
          scheduled_for: scheduling.scheduledTime,
          variables_override: templateVariables,
          effects: visualEffects,
          background_type: backgroundType,
          background_palette: backgroundPalette,
          text_animation: textAnimation,
        });
        toast.success(t('modal.product.toast.approved'));
        onClose();
      } catch (error) {
        console.error('Product approval error:', error);
        toast.error(t('modal.product.toast.approveError', { error: error.message }));
      } finally {
        setIsApprovingProduct(false);
      }
    };

    const handleRegenerateCaption = async () => {
      setIsRegeneratingCaption(true);
      try {
        const response = await axiosInstance.post(
          `/ecommerce/proposals/${item.proposal_id}/regenerate-caption`,
          {},
          { params: { instagram_account_id: selectedAccountId } }
        );
        if (response.data?.caption_text) {
          setProductCaption(response.data.caption_text);
          toast.success(t('modal.product.toast.captionRegenerated', { defaultValue: 'Caption regenerated!' }));
        }
      } catch (error) {
        console.error('Caption regeneration error:', error);
        toast.error(t('modal.product.toast.captionRegenerateError', { defaultValue: 'Failed to regenerate caption', error: error.message }));
      } finally {
        setIsRegeneratingCaption(false);
      }
    };

    const handleRejectProductClick = async () => {
      setIsRejectingProduct(true);
      try {
        await onRejectProduct(item, productRejectReason);
        toast.success(t('modal.product.toast.rejected'));
        onClose();
      } catch (error) {
        console.error('Product rejection error:', error);
        toast.error(t('modal.product.toast.rejectError', { error: error.message }));
      } finally {
        setIsRejectingProduct(false);
        setShowRejectProductConfirm(false);
      }
    };

    const handleTemplateVariablesChange = (newVariables) => {
      setTemplateVariables(newVariables);
    };

    /**
     * Apply all AI suggestions at once (excluding template change - template is fixed)
     */
    const handleApplyAISuggestion = (suggestion) => {
      if (!suggestion) return;

      // Note: Template is fixed after e-commerce selection, not changeable here

      // Apply effects if suggested
      if (suggestion.effects) {
        setVisualEffects(suggestion.effects);
      }

      // Apply style/colors if suggested
      if (suggestion.style) {
        if (suggestion.style.captionColor) {
          setCaptionColor(suggestion.style.captionColor);
        }
        // Apply background type and palette
        if (suggestion.style.backgroundType) {
          setBackgroundType(suggestion.style.backgroundType);
        }
        if (suggestion.style.backgroundPalette) {
          setBackgroundPalette(suggestion.style.backgroundPalette);
        }
      }

      // Apply texts if suggested
      if (suggestion.texts?.caption) {
        setProductCaption(suggestion.texts.caption);
      }

      setAiSuggestionApplied(true);
    };

    /**
     * Build AI suggestion object from item data
     */
    const aiSuggestion = item?.llm_suggestion ? {
      templateId: item.llm_suggestion.templateId || item.template_id,
      effects: item.llm_suggestion.effects,
      style: item.llm_suggestion.style,
      texts: item.llm_suggestion.texts,
      reasoning: item.llm_suggestion.reasoning,
    } : null;

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        title={t('modal.product.title')}
        closeOnBackdrop={false}
        closeOnEscape={!isApprovingProduct && !isRejectingProduct}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 -mx-6 -my-4 lg:h-[calc(100vh-160px)]">
          {/* Left Column - Live Remotion Preview */}
          <div className="lg:sticky lg:top-0 lg:self-start bg-gray-50 p-6 lg:h-[calc(100vh-160px)] flex items-center justify-center">
            <div className="w-full max-w-sm">
              <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
                {t('modal.preview.title')}
              </h3>
              {isRendering ? (
                // Show static preview with rendering overlay when video is being rendered
                <VideoTemplatePreview
                  previewUrl={previewUrl}
                  templateId={item.template_id}
                  variables={templateVariables}
                  isRendering={isRendering}
                />
              ) : (
                // Show live Remotion preview for real-time editing
                // Match video element sizing: full width, 9:16 aspect ratio
                <div className="bg-gray-900 rounded-lg overflow-hidden">
                  <RemotionPreview
                    templateId={item.template_id}
                    templateVariables={{
                      ...templateVariables,
                      // Ensure headline_text is always present (fallback to LLM suggestion or default)
                      headline_text: templateVariables?.headline_text
                        || item?.llm_suggestion?.style?.headlineText
                        || 'Für dich entdeckt',
                    }}
                    captionText={productCaption}
                    captionColor={captionColor}
                    captionFont={captionFont}
                    effects={visualEffects}
                    backgroundType={backgroundType}
                    backgroundPalette={backgroundPalette}
                    textAnimation={textAnimation}
                    controls={true}
                    autoPlay={false}
                    loop={true}
                    className="w-full h-auto"
                    style={{ aspectRatio: '9/16', maxHeight: 'calc(100vh - 280px)' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Content & Controls */}
          <div className="flex flex-col lg:h-[calc(100vh-160px)]">
            {/* Scrollable Content */}
            <div className="flex-1 p-6 space-y-6 overflow-y-auto overscroll-contain">
              {/* Product Info Summary */}
              <div className="bg-purple-50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-purple-900">{productTitle}</h4>
                <div className="flex items-baseline gap-2">
                  {salePrice && (
                    <span className="text-xl font-bold text-purple-600">{salePrice}</span>
                  )}
                  {originalPrice && (
                    <span className="text-sm text-gray-500 line-through">{originalPrice}</span>
                  )}
                  {discountPercent && (
                    <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                      -{discountPercent}%
                    </span>
                  )}
                </div>
              </div>

              {/* Global AI Suggestion Banner */}
              {aiSuggestion && (
                <GlobalAISuggestionBanner
                  aiSuggestion={aiSuggestion}
                  onApply={handleApplyAISuggestion}
                  isApplied={aiSuggestionApplied}
                  disabled={isRendering || isApprovingProduct}
                />
              )}

              {/* Template Settings Accordion - FIRST (most important for customization) */}
              <Accordion
                title={t('modal.product.templateSettingsTitle', { defaultValue: 'Template Settings' })}
                icon="⚙️"
                defaultExpanded={true}
                variant="neutral"
              >
                <div className="pt-4">
                  <TemplateVariableEditor
                    templateId={selectedTemplate}
                    variables={templateVariables}
                    onChange={handleTemplateVariablesChange}
                    disabled={isRendering || isApprovingProduct}
                  />
                </div>
              </Accordion>

              {/* Caption & Text Accordion - Consolidated */}
              <Accordion
                title={t('modal.product.captionTextTitle', { defaultValue: 'Caption & Text' })}
                icon="✏️"
                defaultExpanded={true}
                variant="neutral"
              >
                <div className="pt-4 space-y-6">
                  {/* Caption Text */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('modal.product.captionLabel', { defaultValue: 'Caption Text' })}
                    </label>
                    <textarea
                      value={productCaption}
                      onChange={(e) => setProductCaption(e.target.value)}
                      disabled={isRendering || isApprovingProduct || isRegeneratingCaption}
                      placeholder={t('modal.product.captionPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                      rows={4}
                    />
                    <p className="text-xs text-gray-500">
                      {productCaption.length} / 200 {t('modal.product.captionCounter', { defaultValue: 'characters' })}
                    </p>
                  </div>

                  {/* Caption Color Picker */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      {t('modal.product.captionColorLabel', { defaultValue: 'Text Color' })}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={captionColor}
                        onChange={(e) => setCaptionColor(e.target.value)}
                        disabled={isRendering || isApprovingProduct}
                        className="w-10 h-10 p-1 border border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
                      />
                      {/* Quick Presets */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setCaptionColor('#ffffff')}
                          className={`w-8 h-8 rounded-full border-2 bg-white ${captionColor === '#ffffff' ? 'border-purple-500' : 'border-gray-300'}`}
                          title="White"
                          disabled={isRendering || isApprovingProduct}
                        />
                        <button
                          type="button"
                          onClick={() => setCaptionColor('#000000')}
                          className={`w-8 h-8 rounded-full border-2 bg-black ${captionColor === '#000000' ? 'border-purple-500' : 'border-gray-300'}`}
                          title="Black"
                          disabled={isRendering || isApprovingProduct}
                        />
                        <button
                          type="button"
                          onClick={() => setCaptionColor('#FFD700')}
                          className={`w-8 h-8 rounded-full border-2 ${captionColor === '#FFD700' ? 'border-purple-500' : 'border-gray-300'}`}
                          style={{ backgroundColor: '#FFD700' }}
                          title="Gold"
                          disabled={isRendering || isApprovingProduct}
                        />
                        <button
                          type="button"
                          onClick={() => setCaptionColor('#4ECDC4')}
                          className={`w-8 h-8 rounded-full border-2 ${captionColor === '#4ECDC4' ? 'border-purple-500' : 'border-gray-300'}`}
                          style={{ backgroundColor: '#4ECDC4' }}
                          title="Teal"
                          disabled={isRendering || isApprovingProduct}
                        />
                      </div>
                      <span className="text-sm text-gray-500">{captionColor.toUpperCase()}</span>
                    </div>
                  </div>

                  {/* Caption Font Selector */}
                  <div className="border-t border-gray-200 pt-4">
                    <ProductFontSelector
                      captionFont={captionFont}
                      onCaptionFontChange={setCaptionFont}
                      llmSuggested={item?.llm_suggestion?.style || null}
                      accountId={selectedAccountId}
                      compact={false}
                    />
                  </div>

                  {/* Regenerate Caption Button */}
                  <div className="border-t border-gray-200 pt-4">
                    <button
                      type="button"
                      onClick={handleRegenerateCaption}
                      disabled={isRendering || isApprovingProduct || isRegeneratingCaption}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      <Sparkles size={16} />
                      {isRegeneratingCaption
                        ? t('modal.product.regeneratingCaption', { defaultValue: 'Regenerating...' })
                        : t('modal.product.regenerateCaption', { defaultValue: 'Regenerate Caption' })
                      }
                    </button>
                  </div>
                </div>
              </Accordion>

              {/* Visual Style Accordion - Consolidated */}
              <Accordion
                title={t('modal.product.visualStyleTitle', { defaultValue: 'Visual Style' })}
                icon="🎨"
                defaultExpanded={false}
                variant="neutral"
              >
                <div className="pt-4 space-y-6">
                  {/* Appearance Effects / Text Animation */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      {t('modal.product.appearanceEffects', { defaultValue: 'Appearance Effects' })}
                    </h4>
                    <TextAnimationSelector
                      textAnimation={textAnimation}
                      onTextAnimationChange={setTextAnimation}
                      llmSuggested={item?.llm_suggestion?.style || null}
                      compact={false}
                    />
                  </div>

                  {/* Background Selector */}
                  <div className="border-t border-gray-200 pt-4">
                    <BackgroundSelector
                      backgroundType={backgroundType}
                      backgroundPalette={backgroundPalette}
                      onBackgroundTypeChange={setBackgroundType}
                      onBackgroundPaletteChange={setBackgroundPalette}
                      llmSuggested={item?.llm_suggestion?.style || null}
                      compact={false}
                    />
                  </div>

                  {/* Visual Effects Panel */}
                  <div className="border-t border-gray-200 pt-4">
                    <VisualEffectsPanel
                      effects={visualEffects}
                      onChange={setVisualEffects}
                      llmSuggested={item?.llm_suggestion?.effects ? {
                        ...item.llm_suggestion.effects,
                        reasoning: item.llm_suggestion.reasoning
                      } : null}
                      disabled={isRendering || isApprovingProduct}
                    />
                  </div>
                </div>
              </Accordion>

              {/* Scheduling Accordion */}
              <SchedulingAccordion
                autoScheduledTime={scheduling.autoScheduledTime}
                manualScheduledTime={scheduling.manualScheduledTime}
                scheduledTime={scheduling.scheduledTime}
                isManualMode={scheduling.isManualMode}
                cascadeInfo={scheduling.cascadeInfo}
                postIntervalHours={scheduling.postIntervalHours}
                isLoading={scheduling.isLoading}
                error={scheduling.error}
                onManualTimeChange={scheduling.setManualTime}
                onClearManualTime={scheduling.clearManualTime}
                disabled={isRendering || isApprovingProduct || isRejectingProduct}
              />

              {/* Reject Accordion */}
              <Accordion
                title={t('modal.product.rejectTitle')}
                icon="⚠️"
                defaultExpanded={false}
                variant="danger"
              >
                <div className="space-y-4 pt-4">
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <p className="text-sm text-red-800">{t('modal.product.rejectWarning')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('modal.product.rejectReasonLabel')}
                    </label>
                    <textarea
                      value={productRejectReason}
                      onChange={(e) => setProductRejectReason(e.target.value)}
                      placeholder={t('modal.product.rejectReasonPlaceholder')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                      rows={3}
                      disabled={isRejectingProduct}
                    />
                  </div>

                  <Button
                    variant="danger"
                    onClick={handleRejectProductClick}
                    loading={isRejectingProduct}
                    disabled={isRejectingProduct || isApprovingProduct}
                    className="w-full"
                  >
                    {isRejectingProduct ? t('modal.product.rejecting') : t('modal.product.rejectButton')}
                  </Button>
                </div>
              </Accordion>
            </div>

            {/* Sticky Action Footer */}
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 shadow-lg">
              <div className="flex gap-3 justify-end">
                <Button
                  variant="secondary"
                  onClick={onClose}
                  disabled={isApprovingProduct || isRejectingProduct || isRegeneratingCaption}
                >
                  {t('modal.button.cancel')}
                </Button>

                <Button
                  variant="primary"
                  onClick={handleApproveProductClick}
                  loading={isApprovingProduct}
                  disabled={isRendering || isRejectingProduct || isRegeneratingCaption}
                >
                  {isApprovingProduct ? t('modal.product.approving') : t('modal.product.approveButton')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="5xl"
      title={t('modal.title')}
      closeOnBackdrop={false}
      closeOnEscape={!isApproving}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 -mx-6 -my-4 lg:h-[calc(100vh-160px)]">
        {/* Left Column - Preview */}
        <div className="lg:sticky lg:top-0 lg:self-start bg-gray-50 p-6 lg:h-[calc(100vh-160px)] flex items-center justify-center">
          <div className="w-full max-w-sm">
            <h3 className="text-sm font-medium text-gray-700 mb-3 text-center">
              {t('modal.preview.title')}
            </h3>
            <div className="relative">
              <LivePreviewCanvas
                imageUrl={imageUrl}
                captionText={debouncedCaptionForPreview}
                style={style}
                textPosition={textPosition}
                originalPosition={originalPosition}
                onPositionChange={handlePositionChange}
                onResetPosition={handleResetPosition}
                textAreaWidth={textAreaWidth}
                textAlignment={textAlignment}
                fontSize={fontSize}
                isPositionOverridden={hasPositionChanges}
                // GIF sticker
                gifMetadata={gifMetadata}
                onGifChange={setGifMetadata}
                // Background configuration
                canvasBackgroundColor={canvasBackgroundColor}
                backgroundMode={backgroundMode}
                backgroundConfig={backgroundConfig}
                hasWrongAspectRatio={hasWrongAspectRatio}
                backgroundMetadata={item?.background_metadata}
                // Font
                fontName={selectedFont}
              />
              <AspectRatioIndicator visible={hasWrongAspectRatio} />
            </div>
          </div>
        </div>

        {/* Right Column - Controls */}
        <div className="flex flex-col lg:h-[calc(100vh-160px)]">
          {/* Scrollable Content */}
          <div className="flex-1 p-6 space-y-6 overflow-y-auto overscroll-contain">
            {/* Text & Caption Accordion */}
            <Accordion
              title={t('modal.accordion.textCaptionTitle', { defaultValue: 'Text & Caption' })}
              icon="✏️"
              defaultExpanded={false}
              variant="neutral"
            >
              <div className="space-y-6 pt-4">
                {/* Caption Editor */}
                <CaptionEditor
                  initialCaption={caption}
                  imageId={item.image_id}
                  captionId={item.caption_id || item.current_caption_id}
                  instagramAccountId={selectedAccountId}
                  onChange={handleCaptionChange}
                  onSave={() => {}}
                />

                {/* Style Panel */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">
                    {t('modal.textStyling', { defaultValue: 'Text Styling' })}
                  </h4>
                  <StylePanel
                    style={style}
                    onChange={handleStyleChange}
                    imageUrl={imageUrl}
                    brandColors={brandColors}
                    selectedFont={selectedFont}
                    onFontChange={handleFontChange}
                    accountId={selectedAccountId}
                  />
                </div>

                {/* Alignment Selector */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-medium text-gray-700 mb-4">
                    {t('modal.textPosition', { defaultValue: 'Text Position' })}
                  </h4>
                  <AlignmentSelector
                    currentAlignment={textAlignment}
                    originalAlignment={originalAlignment}
                    onAlignmentChange={handleAlignmentChange}
                    disabled={isSaving || isApproving || isRegenerating}
                  />
                </div>
              </div>
            </Accordion>

            {/* GIF Stickers Accordion */}
            <Accordion
              title={t('upload:gif.stickerLabel', { defaultValue: 'GIF Stickers' })}
              icon="✨"
              defaultExpanded={false}
              variant="neutral"
            >
              <div className="pt-4">
                <button
                  onClick={handleOpenGiphyPanel}
                  className="gif-sticker-btn w-full"
                  disabled={isSavingGif || isRemovingGif}
                >
                  <Sparkles size={16} />
                  {isSavingGif ? (
                    t('upload:gif.saving') || 'Saving...'
                  ) : gifMetadata ? (
                    t('upload:gif.editButton', { count: 1 })
                  ) : (
                    t('upload:gif.addButton')
                  )}
                </button>

                {/* GIF Controls Panel */}
                {gifMetadata && (
                  <div className="mt-4">
                    <GIFControls
                      gifMetadata={gifMetadata}
                      onChange={setGifMetadata}
                      onRemove={handleRemoveGif}
                      isRemoving={isRemovingGif}
                    />
                  </div>
                )}
              </div>
            </Accordion>

            {/* Canvas Background Accordion (only for non-9:16 images) */}
            {hasWrongAspectRatio && (
              <Accordion
                title={t('modal.accordion.canvasBackgroundTitle', { defaultValue: 'Canvas Background' })}
                icon="🎨"
                defaultExpanded={false}
                variant="info"
              >
                <div className="pt-4">
                  <CanvasColorEditor
                    // Background mode & config
                    backgroundMode={backgroundMode}
                    backgroundConfig={backgroundConfig}
                    originalBackgroundConfig={originalBackgroundConfig}
                    // Handlers
                    onModeChange={handleBackgroundModeChange}
                    onSolidColorChange={handleSolidColorChange}
                    onGradientChange={handleGradientChange}
                    // UI state
                    imageUrl={imageUrl}
                    brandColors={brandColors}
                  />
                </div>
              </Accordion>
            )}

            {/* AI Assistant Section - Always Visible */}
            <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg p-4 border border-purple-200">
              <AIAssistant
                currentCaption={caption}
                onRegenerate={handleRegenerate}
                isLoading={isRegenerating}
                imageId={item.image_id}
              />
            </div>

            {/* Scheduling Accordion */}
            <SchedulingAccordion
              autoScheduledTime={scheduling.autoScheduledTime}
              manualScheduledTime={scheduling.manualScheduledTime}
              scheduledTime={scheduling.scheduledTime}
              isManualMode={scheduling.isManualMode}
              cascadeInfo={scheduling.cascadeInfo}
              postIntervalHours={scheduling.postIntervalHours}
              isLoading={scheduling.isLoading}
              error={scheduling.error}
              onManualTimeChange={scheduling.setManualTime}
              onClearManualTime={scheduling.clearManualTime}
              disabled={isApproving || isSaving || isRegenerating}
            />

            {/* Delete Image Accordion */}
            <Accordion
              title={t('modal.accordion.deleteTitle')}
              icon="⚠️"
              defaultExpanded={false}
              variant="danger"
            >
              <div className="space-y-4 pt-4">
                {/* Warning Message */}
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <svg
                    className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm text-red-800">
                    {t('modal.delete.warning')}
                  </p>
                </div>

                {/* Reason Textarea */}
                <div>
                  <label
                    htmlFor="deleteReason"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('modal.delete.reasonLabel')}
                  </label>
                  <textarea
                    id="deleteReason"
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
                    placeholder={t('modal.delete.reasonPlaceholder')}
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    disabled={isDeleting}
                  />
                </div>

                {/* Delete Button */}
                <Button
                  variant="danger"
                  onClick={handleDeleteImage}
                  loading={isDeleting}
                  disabled={!deleteReason.trim() || isDeleting}
                  className="w-full"
                >
                  {isDeleting ? t('modal.delete.deleting') : t('modal.delete.button')}
                </Button>
              </div>
            </Accordion>
          </div>

          {/* Sticky Action Footer */}
          <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 shadow-lg">
            <div className="flex justify-between items-center gap-3">
              {/* Left side - Change indicator */}
              <div className="flex items-center gap-2">
                {(hasStyleChanges || hasCaptionChanges || hasPositionChanges || hasAlignmentChanges || hasBackgroundChanges || hasFontChanges) && (
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full">
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('modal.unsavedChanges', { defaultValue: 'Unsaved changes' })}
                  </span>
                )}
              </div>

              {/* Right side - Action buttons */}
              <div className="flex gap-3">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isApproving || isSaving || isRegenerating}
                >
                  {t('modal.button.cancel')}
                </Button>

                {/* Show either Regenerate or Approve button based on changes */}
                {(hasStyleChanges || hasCaptionChanges || hasPositionChanges || hasAlignmentChanges || hasBackgroundChanges || hasFontChanges) ? (
                  <Button
                    variant="primary"
                    onClick={handleRegenerateWithStyle}
                    loading={isRegenerating}
                    disabled={isSaving || isApproving}
                    className="min-w-[180px]"
                  >
                    {isRegenerating ? t('modal.button.regenerating') : t('modal.button.regenerate')}
                    {!isRegenerating && (hasStyleChanges || hasCaptionChanges || hasPositionChanges || hasAlignmentChanges || hasFontChanges) && (
                      <span className="ml-1">
                        (
                        {[
                          hasStyleChanges && t('modal.changes.style', { defaultValue: 'Style' }),
                          hasCaptionChanges && t('modal.changes.caption', { defaultValue: 'Caption' }),
                          hasPositionChanges && t('modal.changes.position', { defaultValue: 'Position' }),
                          hasAlignmentChanges && t('modal.changes.alignment', { defaultValue: 'Alignment' }),
                          hasBackgroundChanges && (backgroundMode === 'gradient' ? t('modal.changes.gradient', { defaultValue: 'Gradient' }) : t('modal.changes.background', { defaultValue: 'Background' })),
                          hasFontChanges && t('modal.changes.font', { defaultValue: 'Font' })
                        ].filter(Boolean).join(', ')}
                        )
                      </span>
                    )}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    onClick={handleApprove}
                    loading={isApproving}
                    disabled={isSaving || !caption.trim() || isRegenerating}
                    className="min-w-[180px]"
                  >
                    {isApproving ? t('modal.button.approving') : t('modal.button.approve')}
                  </Button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {showGiphyPanel && (
        <GiphyPanel
          onSelectGif={handleSelectGif}
          onClose={() => setShowGiphyPanel(false)}
        />
      )}
    </Modal>
  );
};

// PropTypes validation
ApprovalModalV2.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.oneOf(['image', 'video', 'product']),
    // Image fields (required for images)
    image_id: PropTypes.string,
    // Video fields (required for videos)
    video_id: PropTypes.string,
    file_name: PropTypes.string,
    duration: PropTypes.number,
    file_size: PropTypes.number,
    resolution: PropTypes.string,
    // Product fields (required for products)
    id: PropTypes.string,
    proposal_id: PropTypes.string,
    product_id: PropTypes.string,
    template_id: PropTypes.string,
    template_variables: PropTypes.object,
    caption_color: PropTypes.string,
    // Image URL fields (fallback chain)
    original_sas_url: PropTypes.string,
    rendered_sas_url: PropTypes.string,
    sas_url: PropTypes.string,
    preview_url: PropTypes.string,
    blob_url: PropTypes.string,
    rendered_blob_path: PropTypes.string,
    blob_path: PropTypes.string,
    caption: PropTypes.shape({
      original_sas_url: PropTypes.string,
      rendered_sas_url: PropTypes.string,
      sas_url: PropTypes.string,
      text_position: PropTypes.object,
      text_area_width: PropTypes.number,
      text_area_height: PropTypes.number,
      text_alignment: PropTypes.string,
      recommended_font_size: PropTypes.number
    }),
    // Caption and style fields
    caption_text: PropTypes.string,
    text_color_hex: PropTypes.string,
    text_background_color_hex: PropTypes.string,
    text_has_background: PropTypes.bool,
    // Text rendering fields (from GPT-5 Vision)
    text_position: PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number
    }),
    text_area_width: PropTypes.number,
    text_area_height: PropTypes.number,
    text_alignment: PropTypes.string,
    recommended_font_size: PropTypes.number,
    status: PropTypes.string
  }),
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApprove: PropTypes.func,
  onApproveVideo: PropTypes.func,
  onDeleteVideo: PropTypes.func,
  onApproveProduct: PropTypes.func,
  onRejectProduct: PropTypes.func,
  onUpdate: PropTypes.func,
  onRejectImage: PropTypes.func
};

export default ApprovalModalV2;